import React, { useEffect, useState, useRef, useMemo } from "react"
import MetaTags from 'react-meta-tags';
import { Container, Row, Col, Card, CardBody, Button, Modal, Badge } from "reactstrap"
import Datatables from 'pages/utils/table/datatable';
import "flatpickr/dist/themes/material_blue.css"
import Flatpickr from "react-flatpickr"
import Select from "react-select"
import moment from "moment";
import { post, get, put, del } from 'helpers/api_helper';
import { VOUCHER_URL, PATIENT_URL, DOCTOR_URL } from 'helpers/url_helper';
import { useTranslation } from "react-i18next";
import { showSuccessAlert, showErroeAlert, showConfirmAlert } from "pages/utils/alertMessages";
import cookieHelper from 'helpers/getCookieData';
import { openVoucherPrintWindow } from 'pages/utils/printHelpers';
import {
  RiPrinterLine,
  RiEditLine,
  RiDeleteBinLine,
  RiAddLine,
  RiCheckLine,
  RiReceiptLine
} from 'react-icons/ri';

// Auto-Suggest input for Paid By / Paid To
const PayeeAutoSuggestInput = ({
    value,
    onChange,
    placeholder,
    voucherType, // '1' = Receipt (Paid By), '2' = Payment (Paid To)
    suggestions = [],
    className = "form-control"
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filtered = useMemo(() => {
        if (!suggestions || suggestions.length === 0) return [];
        if (!value || typeof value !== 'string' || value.trim().length === 0) {
            return suggestions.slice(0, 6); // Show top 6 on initial focus
        }
        const lower = value.toLowerCase().trim();
        return suggestions.filter(item => {
            const nameMatch = item.name && item.name.toLowerCase().includes(lower);
            const detailMatch = item.detail && item.detail.toLowerCase().includes(lower);
            return nameMatch || detailMatch;
        }).slice(0, 8);
    }, [value, suggestions]);

    const handleSelect = (item) => {
        onChange(item.name);
        setIsOpen(false);
        setSelectedIndex(-1);
    };

    const handleKeyDown = (e) => {
        if (!isOpen || filtered.length === 0) {
            if (e.key === 'ArrowDown') setIsOpen(true);
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => (prev < filtered.length - 1 ? prev + 1 : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => (prev > 0 ? prev - 1 : filtered.length - 1));
        } else if (e.key === 'Enter') {
            if (selectedIndex >= 0 && selectedIndex < filtered.length) {
                e.preventDefault();
                handleSelect(filtered[selectedIndex]);
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    return (
        <div ref={wrapperRef} className="position-relative w-100">
            <input
                type="text"
                className={className}
                placeholder={placeholder}
                value={value || ''}
                onFocus={() => setIsOpen(true)}
                onChange={(e) => {
                    onChange(e.target.value);
                    setIsOpen(true);
                    setSelectedIndex(-1);
                }}
                onKeyDown={handleKeyDown}
                autoComplete="off"
            />
            {isOpen && filtered.length > 0 && (
                <div
                    className="position-absolute bg-white border rounded shadow-lg w-100 mt-1 py-1"
                    style={{
                        zIndex: 1060,
                        maxHeight: '220px',
                        overflowY: 'auto',
                        minWidth: '250px',
                        top: '100%',
                        left: 0
                    }}
                >
                    <div className="px-2.5 py-1 text-muted font-size-10 text-uppercase fw-bold border-bottom bg-light d-flex justify-content-between align-items-center">
                        <span>{voucherType === '1' ? t("Suggested Payers (Patients / Staff)") : t("Suggested Payees (Staff / Vendors)")}</span>
                        <span className="badge bg-secondary-subtle text-secondary">{filtered.length}</span>
                    </div>
                    {filtered.map((item, idx) => {
                        const isHighlighted = idx === selectedIndex;
                        return (
                            <div
                                key={idx}
                                className={`px-2.5 py-2 cursor-pointer d-flex align-items-center justify-content-between border-bottom border-light ${isHighlighted ? 'bg-primary-subtle' : 'hover-bg-light'}`}
                                style={{ cursor: 'pointer' }}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSelect(item);
                                }}
                                onMouseEnter={() => setSelectedIndex(idx)}
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <span className={`badge ${
                                        item.type === 'Patient' ? 'bg-primary-subtle text-primary border border-primary-subtle' :
                                        item.type === 'Doctor' ? 'bg-success-subtle text-success border border-success-subtle' :
                                        'bg-warning-subtle text-warning border border-warning-subtle'
                                    } font-size-10 px-1.5 py-0.5 rounded-pill`}>
                                        {item.type === 'Patient' ? '👤 Patient' : item.type === 'Doctor' ? '🩺 Doctor' : '🏢 Vendor'}
                                    </span>
                                    <div>
                                        <div className="font-size-12 fw-bold text-dark">{item.name}</div>
                                        {item.detail && (
                                            <div className="font-size-10 text-muted">{item.detail}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const ManagePettyCash = () => {
    const { t } = useTranslation();
    const [isForm, setIsForm] = useState(false);
    const [editId, setEditId] = useState(null);
    const [rows, setRows] = useState({});
    const [totalValue, setTotalValue] = useState(0);

    const clinic = useMemo(() => {
        try {
            const raw = cookieHelper.getCookie('_c');
            return raw ? JSON.parse(atob(raw)) : null;
        } catch (e) {
            return null;
        }
    }, []);

    const [payeeSuggestions, setPayeeSuggestions] = useState({
        receiptPayees: [],
        paymentPayees: []
    });

    const Reciept = [
        { value: 'Cash_Recived', label: 'Cash Recived' },
    ];

    const Payment = [
        { value: "Convience_expenses", label: "Convience expenses" },
        { value: "Electricity_expenses", label: "Electricity expenses" },
        { value: "Telephone_expenses", label: "Telephone expenses" },
        { value: "Staff_welfare_expenses", label: "Staff welfare expenses" },
        { value: "Printing_Stationary", label: "Printing Stationary" },
    ];

    const [voucherData, setVoucherData] = useState({
        voucherDate: moment().format('YYYY-MM-DD HH:mm'),
        voucherType: [{ value: '1', label: 'Receipt' }],
        voucherEntries: [],
        currentEntry: {
            type: { value: '1', label: 'Receipt' },
            amount: '',
            remark: ''
        },
        remark: ''
    });

    // Fetch Patient and Doctor records for Auto-Suggest
    useEffect(() => {
        const loadPayeeDirectories = async () => {
            try {
                const [patientsRes, doctorsRes] = await Promise.all([
                    get(`${PATIENT_URL}?limit=all`),
                    get(`${DOCTOR_URL}?limit=all`)
                ]);

                const pList = patientsRes?.items || patientsRes?.body?.items || (Array.isArray(patientsRes?.body) ? patientsRes.body : (Array.isArray(patientsRes) ? patientsRes : []));
                const dList = doctorsRes?.items || doctorsRes?.body?.items || (Array.isArray(doctorsRes?.body) ? doctorsRes.body : (Array.isArray(doctorsRes) ? doctorsRes : []));

                const patientItems = pList.map(p => {
                    const fullName = `${p.title || ''} ${p.first_name || ''} ${p.last_name || ''}`.trim() || p.full_name || p.mobile || 'Patient';
                    const detailParts = [];
                    if (p.case_no) detailParts.push(`#${p.case_no}`);
                    if (p.mobile) detailParts.push(`📞 ${p.mobile}`);
                    return {
                        name: fullName,
                        detail: detailParts.join(' • '),
                        type: 'Patient'
                    };
                });

                const doctorItems = dList.map(d => {
                    const fullName = `${d.first_name || ''} ${d.last_name || ''}`.trim() || d.name || 'Doctor';
                    const nameWithPrefix = fullName.startsWith('Dr.') ? fullName : `Dr. ${fullName}`;
                    return {
                        name: nameWithPrefix,
                        detail: d.specialist || 'Consultant / Staff',
                        type: 'Doctor'
                    };
                });

                const standardVendors = [
                    { name: "Electricity Board (MSEB / Discom)", detail: "Utility Bill", type: "Vendor" },
                    { name: "Dental Prosthetics & Crown Lab", detail: "Lab Technician", type: "Vendor" },
                    { name: "Dental Surgical & Materials Supplier", detail: "Consumables & Implants", type: "Vendor" },
                    { name: "Office Stationery & Printing Services", detail: "Clinic Supplies", type: "Vendor" },
                    { name: "Staff Salary / Overtime / Advance", detail: "Payroll", type: "Doctor" },
                    { name: "Courier & Express Delivery", detail: "Logistics", type: "Vendor" },
                    { name: "Tea, Water & Clinic Hospitality", detail: "Pantry", type: "Vendor" },
                    { name: "Clinic Premises Rent & Maintenance", detail: "Infrastructure", type: "Vendor" },
                    { name: "Bio-Medical Waste Management Agency", detail: "Statutory", type: "Vendor" }
                ];

                setPayeeSuggestions({
                    receiptPayees: [...patientItems, ...doctorItems],
                    paymentPayees: [...doctorItems, ...standardVendors]
                });
            } catch (err) {
                console.error("Error loading payee directory:", err);
            }
        };

        loadPayeeDirectories();
    }, []);

    const activeSuggestions = voucherData?.voucherType[0]?.value === '1'
        ? payeeSuggestions.receiptPayees
        : payeeSuggestions.paymentPayees;

    const voucherOptions = voucherData?.voucherType[0]?.value == 1 ? Reciept : Payment;

    const handleAddNewRow = () => {
        const newRow = {
            type: voucherData.currentEntry.type,
            amount: voucherData.currentEntry.amount,
            remark: voucherData.currentEntry.remark,
        };

        setVoucherData(prev => ({
            ...prev,
            voucherEntries: [...prev.voucherEntries, newRow],
            currentEntry: {
                type: voucherData.voucherType[0]?.value == 1 ? Reciept[0] : Payment[0],
                amount: '',
                remark: ''
            }
        }));
    };

    const handleRemoveRow = (index) => {
        setVoucherData(prev => ({
            ...prev,
            voucherEntries: prev.voucherEntries?.filter((_, i) => i !== index)
        }));
    };

    const handleDelete = async (id, status = 1) => {
        showConfirmAlert(
            t("Are you sure?"),
            t("Do you really want to delete this petty cash voucher entry?"),
            async () => {
                try {
                    const res = await del(`${VOUCHER_URL}/${id}?sts=${status}`);
                    if (res?.success || res) {
                        showSuccessAlert(t("Voucher deleted successfully!"));
                        handleGetAllVoucher();
                    } else {
                        showErroeAlert(res?.message || t("Failed to delete voucher."));
                    }
                } catch (err) {
                    console.error("Error deleting voucher:", err);
                    showErroeAlert(t("Error deleting voucher."));
                }
            }
        );
    };
    
    const handleEdit = async (data) => {
        try {
            const res = await get(`${VOUCHER_URL}/${data.id}`);
            const voucher = res?.body || res?.data || res;
            if (voucher) {
                const typeOpt = String(voucher.receipt_type) === '1' || voucher.receipt_type === 1 
                    ? { value: '1', label: t('Receipt') } 
                    : { value: '2', label: t('Payment') };

                const categoryList = (voucher.receipt_type == 1 ? Reciept : Payment);
                const rawEntries = voucher.voucherEntries || voucher.voucher_transactions || [];

                let formattedEntries = [];
                if (rawEntries.length > 0) {
                    formattedEntries = rawEntries.map(e => {
                        const catKey = e.description || e.type || '';
                        const matchedCat = categoryList.find(c => c.value === catKey || c.label === catKey) || {
                            value: catKey,
                            label: catKey ? String(catKey).replace(/_/g, ' ') : categoryList[0]?.label
                        };
                        return {
                            type: matchedCat,
                            amount: e.amount,
                            remark: e.entity || e.remark || ''
                        };
                    });
                }

                setEditId(voucher.id);
                setVoucherData({
                    voucherDate: voucher.datetime ? moment(voucher.datetime).format('YYYY-MM-DD HH:mm') : moment().format('YYYY-MM-DD HH:mm'),
                    voucherType: [typeOpt],
                    voucherEntries: formattedEntries.length > 1 ? formattedEntries.slice(0, -1) : [],
                    currentEntry: formattedEntries.length > 0 ? formattedEntries[formattedEntries.length - 1] : {
                        type: voucher.receipt_type == 1 ? Reciept[0] : Payment[0],
                        amount: voucher.total_value || '',
                        remark: voucher.transection_description || ''
                    },
                    remark: voucher.remark || ''
                });
                setIsForm(true);
            }
        } catch (e) {
            console.error("Error fetching voucher details for edit:", e);
            showErroeAlert(t("Failed to load voucher details."));
        }
    };

    const handlePrint = async (row) => {
        try {
            const res = await get(`${VOUCHER_URL}/${row.id}`);
            const fullVoucher = res?.body || res?.data || res || row;
            openVoucherPrintWindow(fullVoucher, clinic);
        } catch (e) {
            openVoucherPrintWindow(row, clinic);
        }
    };

    const manageColumns = [
        { 
            dataField: 'id', 
            text: '#',
            style: { width: '45px', textAlign: 'center', verticalAlign: 'middle' },
            formatter: (cell, row, rowIndex) => (
                <span className="badge bg-light text-muted border font-size-11 font-weight-bold">
                    {rowIndex + 1}
                </span>
            )
        },
        { 
            dataField: 'datetime', 
            text: 'Date', 
            style: { verticalAlign: 'middle' },
            formatter: (cell) => (
                <span className="font-size-12 text-dark font-weight-medium">
                    {cell ? moment(cell).format('DD MMM YYYY') : '—'}
                </span>
            )
        },
        {
          dataField: 'receipt_type',
          text: 'Voucher Type',
          style: { verticalAlign: 'middle' },
          formatter: (cell, row) => {
            const isReceipt = String(row.receipt_type) === '1' || row.receipt_type === 1;
            return isReceipt ? (
              <span className="badge bg-success-subtle text-success border border-success-subtle font-size-11 px-2.5 py-1 rounded-pill font-weight-semibold">
                ● {t("Receipt (Inflow)")}
              </span>
            ) : (
              <span className="badge bg-primary-subtle text-primary border border-primary-subtle font-size-11 px-2.5 py-1 rounded-pill font-weight-semibold">
                ● {t("Payment (Expense)")}
              </span>
            );
          }
        }, 
        {
          dataField: 'transection_id',
          text: 'Voucher ID',
          style: { verticalAlign: 'middle' },
          formatter: (cell, row) => (
            <span className="badge bg-light text-primary border font-monospace font-weight-bold font-size-11 px-2 py-1">
              {cell || `VCH-${row.id}`}
            </span>
          )
        }, 
        {
          dataField: 'total_value',
          text: 'Amount',
          style: { verticalAlign: 'middle', textAlign: 'right' },
          headerStyle: { textAlign: 'right' },
          formatter: (cell) => (
            <span className="font-weight-bold text-dark font-size-13">
              ₹{parseFloat(cell || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          )
        }, 
        {
          dataField: 'transection_description',
          text: 'Description / Narration',
          style: { verticalAlign: 'middle' },
          formatter: (cell, row) => (
            <span className="font-size-12 text-muted">
              {cell ? String(cell).replace(/_/g, ' ') : (row.receipt_type == 1 ? 'Cash Received' : 'Cash Paid')}
            </span>
          )
        }, 
        {
          dataField: 'no_of_entries',
          text: 'Entries',
          style: { verticalAlign: 'middle', textAlign: 'center', width: '70px' },
          headerStyle: { textAlign: 'center', width: '70px' },
          formatter: (cell) => (
            <span className="badge bg-light text-dark border font-size-11 px-2 py-1">
              {cell || 1}
            </span>
          )
        }, 
        {
            dataField: 'actions',
            text: 'Actions',
            isDummyField: true,
            style: { verticalAlign: 'middle', textAlign: 'center', width: '130px' },
            headerStyle: { textAlign: 'center', width: '130px' },
            formatter: (cell, row) => (
                <div className="d-flex align-items-center justify-content-center gap-1">
                  <button 
                    type="button" 
                    className="btn btn-sm btn-soft-primary p-1.5 rounded-circle d-flex align-items-center justify-content-center shadow-2xs" 
                    onClick={() => handlePrint(row)} 
                    title={t("Print Voucher")}
                  >
                    <RiPrinterLine className="font-size-14" />
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-sm btn-soft-warning p-1.5 rounded-circle d-flex align-items-center justify-content-center shadow-2xs" 
                    onClick={() => handleEdit(row)} 
                    title={t("Edit Voucher")}
                  >
                    <RiEditLine className="font-size-14 text-warning" />
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-sm btn-soft-danger p-1.5 rounded-circle d-flex align-items-center justify-content-center shadow-2xs" 
                    onClick={() => handleDelete(row.id, row.status == 1 ? 0 : 1)} 
                    title={row.status == 0 ? t('Delete Voucher') : t('Restore Voucher')}
                  >
                    <RiDeleteBinLine className="font-size-14 text-danger" />
                  </button>
                </div>
            )
        }
    ];

    const handleToggle = () => {
        if (isForm) {
            setEditId(null);
            setVoucherData({
                voucherDate: moment().format('YYYY-MM-DD HH:mm'),
                voucherType: [{ value: '1', label: 'Receipt' }],
                voucherEntries: [],
                currentEntry: {
                    type: { value: '1', label: 'Receipt' },
                    amount: '',
                    remark: ''
                },
                remark: ''
            });
        }
        setIsForm(!isForm);
    };

    const handleSubmit = async () => {
        const ent = [...voucherData.voucherEntries];
        if (voucherData.currentEntry && (voucherData.currentEntry.amount || voucherData.currentEntry.remark)) {
            ent.push(voucherData.currentEntry);
        }

        if (ent.length === 0) {
            showErroeAlert(t("Please add at least one entry with an amount."));
            return;
        }

        const validEntries = ent.filter(e => parseFloat(e.amount || 0) > 0);
        if (validEntries.length === 0) {
            showErroeAlert(t("Please enter a valid amount for at least one entry."));
            return;
        }

        const formattedEntries = validEntries.map(entry => ({
            type: typeof entry.type === 'object' ? (entry.type.value || entry.type.label) : entry.type,
            amount: parseFloat(entry.amount),
            remark: entry.remark || ''
        }));

        const Obj = {
            voucherDate: voucherData.voucherDate,
            voucherType: voucherData.voucherType[0].value,
            voucherEntries: formattedEntries,
            remark: voucherData.remark || '',
            totalValue: totalValue,
            clinic_id: clinic?.id || 8,
            client_id: clinic?.client_id || 71
        };
        
        try {
            if (editId) {
                const res = await put(`${VOUCHER_URL}/${editId}`, Obj);
                if (res?.success || res) {
                    showSuccessAlert(t("Voucher updated successfully!"));
                    handleToggle();
                    handleGetAllVoucher();
                } else {
                    showErroeAlert(res?.message || t("Failed to update voucher."));
                }
            } else {
                const res = await post(VOUCHER_URL, Obj);
                if (res?.success || res?.body || res?.data) {
                    showSuccessAlert(t("Voucher created successfully!"));
                    handleToggle();
                    handleGetAllVoucher();
                } else {
                    showErroeAlert(res?.message || t("Failed to create voucher."));
                }
            }
        } catch (err) {
            console.error("Error saving voucher:", err);
            showErroeAlert(err?.response?.data?.message || err.message || t("Failed to save voucher."));
        }
    };

    const handleGetAllVoucher = async (page = 1, limit = 10, search = '') => {
        try {
            let url = `${VOUCHER_URL}?page=${page}&limit=${limit}`;
            if (search) url += `&query=${encodeURIComponent(search)}`;
            const res = await get(url);
            if (res?.success && res?.body) {
                setRows(res.body);
            } else if (res?.items || res?.totalItems) {
                setRows(res);
            } else if (res?.body) {
                setRows(res.body);
            } else if (Array.isArray(res)) {
                setRows({ totalItems: res.length, items: res });
            }
        } catch (e) {
            console.error("Error fetching vouchers:", e);
        }
    };

    useEffect(() => {
        handleGetAllVoucher();
    }, []);

    const handleTotalValueCalc = () => {
        const voucherDataEntries = voucherData.voucherEntries || [];
        const currentEntry = voucherData.currentEntry || {};
        let _value = 0;
        if (voucherDataEntries.length > 0) {
            let totalAmount = voucherDataEntries.reduce((total, entry) => total + parseFloat(entry.amount || 0), 0);
            _value = parseFloat(currentEntry.amount || 0) + totalAmount;
        } else {
            _value = parseFloat(currentEntry.amount || 0);
        }
        setTotalValue(_value);
    };

    useEffect(handleTotalValueCalc, [voucherData]);

    const handleEntryUpdate = (index, field, value) => {
        setVoucherData(prev => ({
            ...prev,
            voucherEntries: prev.voucherEntries.map((entry, i) => 
                i === index ? { ...entry, [field]: value } : entry
            )
        }));
    };

    return (
        <React.Fragment>
            <Datatables
                isSearch={true}
                columns={manageColumns}
                showTableOnly={true}
                rowsLength={rows?.totalItems || 0}
                rows={rows.items || []}
                keyField={'id'}
                handleAddButton={handleToggle}
                title="Petty Cash"
                isAdd={true}
                isTableHead={true}
                ssr={handleGetAllVoucher}
            />

            {/* Start Popup Modal */}
            <Modal size="lg" isOpen={isForm} toggle={handleToggle} centered className="custom-modal">
                <div className="modal-header">
                    <h5 className="modal-title font-weight-bold" id="voucherModalLabel">
                        <i className="bx bx-receipt me-1 font-size-18 text-primary" />
                        {editId ? t("Edit Petty Cash Voucher") : t("Add Petty Cash Voucher")}
                    </h5>
                    <button type="button" className="btn-close" onClick={handleToggle} aria-label="Close"></button>
                </div>
                <div className="modal-body p-4">
                    <Row className="g-3 mb-4">
                        <Col md={6}>
                            <div>
                                <label className="form-label fw-medium font-size-13">{t("Voucher Date & Time")}</label>
                                <Flatpickr
                                    className="form-control"
                                    value={voucherData.voucherDate}
                                    onChange={([date]) =>
                                        setVoucherData(prev => ({
                                            ...prev,
                                            voucherDate: date ? date.toISOString().split("T")[0] : "",
                                        }))
                                    }
                                    options={{
                                        altInput: true,
                                        altFormat: "F j, Y h:i K",
                                        dateFormat: "Y-m-d H:i",
                                        enableTime: true,
                                        time_24hr: false,
                                    }}
                                    placeholder={t("Select Date & Time")}
                                />
                            </div>
                        </Col>
                        <Col md={6}>
                            <div>
                                <label className="form-label fw-medium font-size-13">{t("Voucher Type")}</label>
                                <Select
                                    onChange={e => setVoucherData(prev => ({
                                        ...prev,
                                        voucherType: [e],
                                        currentEntry: {
                                            ...prev.currentEntry,
                                            type: e.value === '1' ? Reciept[0] : Payment[0]
                                        }
                                    }))}
                                    value={voucherData?.voucherType[0]}
                                    classNamePrefix="select2-selection"
                                    options={[
                                        { value: '1', label: t('Receipt') },
                                        { value: '2', label: t('Payment') },
                                    ]}
                                    placeholder={t("Select Type")}
                                />
                            </div>
                        </Col>
                    </Row>

                    {/* Voucher Entries Card */}
                    <div className="p-3 bg-light rounded border mb-3">
                        <h6 className="font-size-13 fw-bold text-muted text-uppercase mb-3">
                            <i className="bx bx-list-plus me-1" />
                            {t("Voucher Entries")}
                        </h6>

                        {/* Table Header Labels */}
                        <Row className="g-2 mb-2 text-muted font-size-12 fw-semibold">
                            <Col md={4}>{t("Category / Ledger")}</Col>
                            <Col md={3}>{t("Amount (₹)")}</Col>
                            <Col md={4}>{t("Paid")} {voucherData?.voucherType[0]?.value === '1' ? t('By') : t('To')}</Col>
                            <Col md={1} className="text-center">{t("Action")}</Col>
                        </Row>

                        {/* Existing entries */}
                        {voucherData.voucherEntries.length > 0 &&
                            voucherData.voucherEntries.map((entry, index) => (
                                <Row key={index} className="g-2 mb-2 align-items-center">
                                    <Col md={4}>
                                        <Select
                                            value={entry.type}
                                            classNamePrefix="select2-selection"
                                            options={voucherOptions}
                                            onChange={e => handleEntryUpdate(index, 'type', e)}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <input
                                            type="number"
                                            className="form-control"
                                            defaultValue={entry.amount}
                                            placeholder={t("Amount")}
                                            onChange={e => handleEntryUpdate(index, 'amount', e.target.value)}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <PayeeAutoSuggestInput
                                            value={entry.remark}
                                            placeholder={`${t("Paid")} ${voucherData?.voucherType[0]?.value === '1' ? t('By') : t('To')}`}
                                            voucherType={voucherData?.voucherType[0]?.value}
                                            suggestions={activeSuggestions}
                                            onChange={val => handleEntryUpdate(index, 'remark', val)}
                                        />
                                    </Col>
                                    <Col md={1} className="text-center">
                                        <Button
                                            color="danger"
                                            size="sm"
                                            className="w-100 p-1"
                                            onClick={() => handleRemoveRow(index)}
                                            title={t("Remove Entry")}
                                        >
                                            <i className="bx bx-trash font-size-14" />
                                        </Button>
                                    </Col>
                                </Row>
                            ))
                        }

                        {/* New Entry Row */}
                        <Row className="g-2 align-items-center">
                            <Col md={4}>
                                <Select
                                    value={voucherData?.currentEntry.type}
                                    classNamePrefix="select2-selection"
                                    options={voucherOptions}
                                    onChange={e => setVoucherData(prev => ({
                                        ...prev,
                                        currentEntry: { ...prev.currentEntry, type: e }
                                    }))}
                                    placeholder={t("Select Category")}
                                />
                            </Col>
                            <Col md={3}>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder={t("Amount")}
                                    value={voucherData?.currentEntry.amount}
                                    onChange={e => setVoucherData(prev => ({
                                        ...prev,
                                        currentEntry: { ...prev.currentEntry, amount: e.target.value }
                                    }))}
                                />
                            </Col>
                            <Col md={4}>
                                <PayeeAutoSuggestInput
                                    value={voucherData?.currentEntry.remark}
                                    placeholder={`${t("Paid")} ${voucherData?.voucherType[0]?.value === '1' ? t('By') : t('To')}`}
                                    voucherType={voucherData?.voucherType[0]?.value}
                                    suggestions={activeSuggestions}
                                    onChange={val => setVoucherData(prev => ({
                                        ...prev,
                                        currentEntry: { ...prev.currentEntry, remark: val }
                                    }))}
                                />
                            </Col>
                            <Col md={1} className="text-center">
                                <Button
                                    color="primary"
                                    size="sm"
                                    className="w-100 p-1"
                                    onClick={handleAddNewRow}
                                    title={t("Add Row")}
                                >
                                    <i className="bx bx-plus font-size-14" />
                                </Button>
                            </Col>
                        </Row>

                        {/* Total Amount Summary */}
                        <div className="d-flex justify-content-end align-items-center mt-3 pt-3 border-top">
                            <span className="text-muted fw-semibold font-size-13 me-2">{t("Total Amount")}:</span>
                            <span className="badge bg-soft-primary text-primary font-size-16 fw-bold px-3 py-2 rounded">
                                ₹{parseFloat(totalValue || 0).toFixed(2)}
                            </span>
                        </div>
                    </div>

                    <div>
                        <label className="form-label fw-medium font-size-13">{t("Remark / Notes")}</label>
                        <textarea
                            className="form-control"
                            rows={3}
                            placeholder={t("Enter any transaction details or notes...")}
                            value={voucherData.remark || ''}
                            onChange={e => setVoucherData(prev => ({
                                ...prev,
                                remark: e.target.value,
                            }))}
                        ></textarea>
                    </div>
                </div>
                <div className="modal-footer border-top">
                    <Button color="light" onClick={handleToggle}>{t("Cancel")}</Button>
                    <Button color="primary" onClick={handleSubmit} className="d-inline-flex align-items-center gap-1">
                        <i className="bx bx-check font-size-16" />
                        {t("Save Voucher")}
                    </Button>
                </div>
            </Modal>
            {/* END Popup Modal */}

        </React.Fragment>
    );
};

export default ManagePettyCash;